import { createSlice } from "@reduxjs/toolkit";
import {
  MccRequest,
  TransactionDownloadRequest,
  TransactionRequest,
  adjustTransaction,
  adjustTransactionRequest,
  blockMerchant,
  createNote,
  deletePhoto,
  downloadTransaction,
  getActivity,
  getActivityRequest,
  getAllTransaction,
  getActivityPreferences,
  listNote,
  mockCardPurchase,
  reverseCashTransaction,
  ReverseCashTransactionRequest,
  searchMCC,
  transactionPhoto,
  updateActivity,
  updateActivityRequest,
  updateGstAmount,
  updateNote,
  CreateNoteRequest
} from "src/network/graphql/transactionService";
import { AccountStatus, User } from "src/types/commonTypes";
import { StoreDispatch } from "../store";
import { Wallet } from "../wallets/walletSlice";
import {
  SearchWalletRequest,
  WalletPreferenceRequest,
  changeWalletPreference,
  getWalletDetail,
  searchWallet
} from "src/network/graphql/walletService";
import { SearchUserRequest, getUserById, searchUser } from "src/network/graphql/userService";
import { immediateToast } from "izitoast-react";
import { initialWalletDetail } from "../walletDetail/walletDetailSlice";
import { initialUserDetail } from "../userDetail/userDetailSlice";
import { initialMccList } from "../mcc/mccSlice";
import { EMPTY_STRING } from "src/components/constant/constant";
import { getGroupList, getGroupUser, SearchGroupListRequest } from "src/network/graphql/groupService";
import { showReportStatusPopup } from "src/components/UI/reportStatusPopup";
import { Group } from "../group/groupSlice";
import { formatFullName, formatSignedDollar } from "src/utils";

export type Transaction = {
  id: string;
  link_id?: string;
  name: string;
  activity_purpose?: string | null;
  Processstatus: string;
  data: {
    description: string;
    merchantName: string;
    merchantId: string;
    merchantCategoryCode: string;
    declineReasonDescription: string;
    name: string;
    timestamp: string;
    transType: string;
    updated_at: string;
    created_at: string;
    amount: number;
    type: string;
    wallet_id: string;
    side: string;
    ppan: string;
    price_components: { gst: number };
    wallet: {
      created_at: string;
      description: string;
      name: string;
      updated_at: string;
      wallet_type: string;
      balance: number;
      display_id: string;
      wallet_id: string;
      object_type: string;
      object_id: string;
    };
    card: {
      card_id: string;
      card_type: string;
      holder_name: string;
    };
    user: {
      attributes: {
        first_name: string;
        last_name: string;
      };
      user_id: string;
      email: string;
    };
    source_wallet: {
      name: string;
      object_id: string;
      wallet_id: string;
      description: string;
    };
    destination_wallet: {
      name: string;
    };
    transaction_user: {
      attributes: {
        first_name: string;
        last_name: string;
      };
      user_id: string;
      email: string;
    };
    group: {
      group_id: string;
      owner_id: string;
      name: string;
      description: string;
      group_type: string;
      group_status: string;
    };
    // Cash reversal. The flagged original carries `reversal_link_id`; the credit row that
    // reverses it carries `reference_link_id` / `reference_activity_id`. Legacy single-row
    // reversals carry neither. All three carry reason / reversed_by / reversed_at.
    reversal_link_id?: string;
    reference_link_id?: string;
    reference_activity_id?: string;
    reason?: string;
    reversed_by?: string;
    reversed_at?: string;
    reversed_by_user?: {
      attributes?: {
        first_name?: string;
        last_name?: string;
      };
      user_id?: string;
      email?: string;
    };
  };
  verified: boolean;
  tagged_users: string[];
  expense_id: string;
  accounting_id: string;
  activityStatus: string;
  endDate: string;
  startDate: string;
  ownerId: string;
  image: string;
  receipt_available: boolean;
  approval_number: string;
  accounting_status: AccountStatus;
  // Present only when Novatti settled for a different amount than we authorised.
  settled_amount?: number;
  settlement_delta?: number;
  settled_at?: string;
  xero_details: {
    // xero_account_id: string;
    // xero_tracking_category_mapping: string;
    xero_bank_transaction_id: string;
  };
};

type ActivityResponse = {
  activity_id: string;
  link_id?: string;
  activity_name: string;
  activity_purpose?: string | null;
  activity_status: string;
  end_timestamp: string;
  created_at: string;
  owner_id: string;
  receipt_available: boolean;
  approval_number: string;
  data: string;
  xero_details?: Transaction["xero_details"];
  attributes?: {
    verified?: boolean;
    tagged_users?: string[];
    expense_id?: string;
    accounting_id?: string;
    accounting_status?: AccountStatus;
    settled_amount?: number;
    settlement_delta?: number;
    settled_at?: string;
  };
};

// search_activity and get_activity return the same Activity fields, so every thunk here maps one
// shape. Fields a query does not select simply land undefined.
export const mapActivityToTransaction = (activity: ActivityResponse) => ({
  id: activity.activity_id,
  link_id: activity.link_id,
  name: activity.activity_name,
  activity_purpose: activity.activity_purpose,
  endDate: activity.end_timestamp,
  startDate: activity.created_at,
  ownerId: activity.owner_id,
  activityStatus: activity.activity_status,
  receipt_available: activity.receipt_available,
  approval_number: activity.approval_number,
  data: JSON.parse(activity?.data),
  xero_details: activity?.xero_details,
  verified: activity?.attributes?.verified,
  tagged_users: activity?.attributes?.tagged_users,
  expense_id: activity?.attributes?.expense_id,
  accounting_id: activity?.attributes?.accounting_id,
  accounting_status: activity?.attributes?.accounting_status,
  settled_amount: activity?.attributes?.settled_amount,
  settlement_delta: activity?.attributes?.settlement_delta,
  settled_at: activity?.attributes?.settled_at
});

const initialTransaction: Transaction[] = [];

export type Note = {
  noteId: string;
  objectId: string;
  message: string;
  updated_at: string;
  created_by: string;
};

const initialNote: Note[] = [];

const initialUsers: User[] = [];

const initialWallets: Wallet[] = [];

const initialGroups: Group[] = [];

type recieptPhoto = {
  data: string;
};

const initialRecieptPhoto: recieptPhoto = {
  data: EMPTY_STRING
};

type uploadRecieptPhoto = {
  data: string;
};

const initialUploadRecieptPhoto: uploadRecieptPhoto = {
  data: EMPTY_STRING
};

type masterList = {
  id: number;
  categoryName: string;
};

const initialMasterList: masterList[] = [];

const transactionSlice = createSlice({
  name: "transactions",
  initialState: {
    transLoading: false,
    userDetailLoading: false,
    mockCardLoading: false,
    userLoading: false,
    walletLoading: false,
    groupLoading: false,
    paymentDataLoading: false,
    deletePhotoLoading: false,
    noteLoading: false,
    walletDetailLoading: false,
    addTransactionLoading: false,
    participantWalletLoading: false,
    blockMerchantLoading: false,
    reverseCashTransactionLoading: false,
    searchMccLoading: false,
    updateActivityLoading: false,
    groupUserLoading: false,
    priceBreakUpLoading: false,
    priceBreakUp: {
      list: [] as Array<{
        user_id: string;
        amount: number;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
      }>,
      total: 0
    },
    downloadTransactionReportId: null as string | null,
    error: null,
    transList: {
      list: initialTransaction,
      total: 0
    },
    userDetail: initialUserDetail,
    users: {
      list: initialUsers,
      total: 0
    },
    walletList: {
      list: initialWallets,
      total: 0
    },
    groupList: {
      list: initialGroups,
      total: 0
    },
    transDetail: {
      list: initialTransaction,
      total: 0
    },
    transRecieptPhoto: initialRecieptPhoto,
    uploadRecieptPhoto: initialUploadRecieptPhoto,
    mastersList: {
      total: 0,
      list: initialMasterList
    },
    notes: initialNote,
    walletDetail: initialWalletDetail,
    participantWallet: initialWalletDetail,
    mccList: {
      list: initialMccList,
      total: 0
    },
    groupUser: {
      list: initialUsers,
      total: 0
    }
  },
  reducers: {
    fetchTransListStart: (state) => {
      state.transLoading = true;
    },
    fetchTransListSuccess: (state, action) => {
      state.transLoading = false;
      state.transList.list = action.payload?.list ?? [];
      state.transList.total = action.payload?.total ?? 0;
    },
    fetchTransListFail: (state, action) => {
      state.transLoading = false;
      state.transList.list = [];
      state.transList.total = 0;
      state.error = action.payload;
    },
    fetchUserDetailStart: (state) => {
      state.userDetailLoading = true;
    },
    fetchUserDetailSuccess: (state, action) => {
      state.userDetailLoading = false;
      state.userDetail = action.payload ?? initialUserDetail;
    },
    fetchUserDetailFail: (state, action) => {
      state.userDetailLoading = false;
      state.userDetail = initialUserDetail;
      state.error = action.payload;
    },
    setUsers: (state, action) => {
      state.users = {
        list: action.payload,
        total: action.payload?.length || 0
      };
    },
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.userLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      state.walletList.list = action.payload?.list ?? [];
      state.walletList.total = action.payload?.total ?? 0;
    },
    fetchWalletsFail: (state, action) => {
      state.walletLoading = false;
      state.walletList.list = [];
      state.walletList.total = 0;
      state.error = action.payload;
    },
    setWallets: (state, action) => {
      state.walletList = {
        list: action.payload,
        total: action.payload?.length || 0
      };
    },
    setGroups: (state, action) => {
      state.groupList = {
        list: action.payload,
        total: action.payload?.length || 0
      };
    },
    fetchGroupsStart: (state) => {
      state.groupLoading = true;
    },
    fetchGroupsSuccess: (state, action) => {
      state.groupLoading = false;
      state.groupList.list = action.payload?.list ?? [];
      state.groupList.total = action.payload?.total ?? 0;
    },
    fetchGroupsFail: (state, action) => {
      state.groupLoading = false;
      state.groupList.list = [];
      state.groupList.total = 0;
      state.error = action.payload;
    },
    downloadTransactionStart: (state) => {
      state.paymentDataLoading = true;
    },
    downloadTransactionSuccess: (state, action) => {
      state.paymentDataLoading = false;
      state.downloadTransactionReportId = action.payload;
    },
    downloadTransactionFail: (state, action) => {
      state.paymentDataLoading = false;
      state.downloadTransactionReportId = null;
      state.error = action.payload;
    },
    mockCardStart: (state) => {
      state.mockCardLoading = true;
      state.error = null;
    },
    mockCardSuccess: (state) => {
      state.mockCardLoading = false;
    },
    mockCardFail: (state, action) => {
      state.mockCardLoading = false;
      state.error = action?.payload;
    },
    getNoteStart: (state) => {
      state.noteLoading = true;
    },
    getNoteSuccess: (state, action) => {
      state.noteLoading = false;
      state.notes = action.payload;
    },
    getNoteFail: (state, action) => {
      state.noteLoading = false;
      state.error = action.payload;
    },
    changeNoteStart: (state) => {
      state.noteLoading = true;
    },
    changeNoteSuccess: (state) => {
      state.noteLoading = false;
    },
    changeNoteFail: (state, action) => {
      state.noteLoading = false;
      state.error = action.payload;
    },
    deletePhotoStart: (state) => {
      state.deletePhotoLoading = true;
    },
    deletePhotoSuccess: (state) => {
      state.deletePhotoLoading = false;
    },
    deletePhotoFail: (state, action) => {
      state.deletePhotoLoading = false;
      state.error = action.payload;
    },
    fetchWalletDetailStart: (state) => {
      state.walletDetailLoading = true;
    },
    fetchWalletDetailSuccess: (state, action) => {
      state.walletDetailLoading = false;
      state.walletDetail.wallet = action.payload.wallet ?? initialWalletDetail.wallet;
    },
    fetchWalletDetailFail: (state, action) => {
      state.walletDetailLoading = false;
      state.walletDetail = initialWalletDetail;
      state.error = action.payload;
    },
    updateGstAmountStart: (state) => {
      state.noteLoading = true;
    },
    updateGstAmountSuccess: (state) => {
      state.noteLoading = false;
    },
    updateGstAmountFail: (state, action) => {
      state.noteLoading = false;
      state.error = action.payload;
    },
    setAddTransactionLoading: (state, action) => {
      state.addTransactionLoading = action.payload;
    },
    addTransactionSuccess: (state, action) => {
      state.transDetail.list = action.payload?.list ?? [];
      state.transDetail.total = action.payload?.total ?? 0;
    },
    addTransactionFail: (state, action) => {
      state.transDetail.list = [];
      state.transDetail.total = 0;
      state.error = action.payload;
    },
    getParticipantWalletStart: (state) => {
      state.participantWalletLoading = true;
    },
    getParticipantWalletSuccess: (state, action) => {
      state.participantWalletLoading = false;
      state.participantWallet = action.payload;
    },
    getParticipantWalletFail: (state, action) => {
      state.participantWalletLoading = false;
      state.error = action.payload;
    },
    blockMerchantStart: (state) => {
      state.blockMerchantLoading = true;
    },
    blockMerchantSuccess: (state) => {
      state.blockMerchantLoading = false;
    },
    blockMerchantFail: (state, action) => {
      state.blockMerchantLoading = false;
      state.error = action.payload;
    },
    reverseCashTransactionStart: (state) => {
      state.reverseCashTransactionLoading = true;
    },
    reverseCashTransactionSuccess: (state) => {
      state.reverseCashTransactionLoading = false;
    },
    reverseCashTransactionFail: (state, action) => {
      state.reverseCashTransactionLoading = false;
      state.error = action.payload;
    },
    fetchMccStart: (state) => {
      state.searchMccLoading = true;
      state.error = null;
    },
    fetchMccSuccess: (state, action) => {
      state.searchMccLoading = false;
      state.error = null;
      state.mccList = action?.payload;
    },
    fetchMccFail: (state, action) => {
      state.searchMccLoading = false;
      state.error = action.payload;
    },
    updateActivityStart: (state) => {
      state.updateActivityLoading = true;
    },
    updateActivitySuccess: (state) => {
      state.updateActivityLoading = false;
    },
    updateActivityFail: (state, action) => {
      state.updateActivityLoading = false;
      state.error = action.payload;
    },
    fetchGroupUserListStart: (state) => {
      state.groupUserLoading = true;
    },
    fetchGroupUserListSuccess: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = action.payload?.list ?? [];
      state.groupUser.total = action.payload?.total ?? 0;
    },
    fetchGroupUserListFail: (state, action) => {
      state.groupUserLoading = false;
      state.error = action.payload;
    },
    resetGroupUserList: (state) => {
      state.groupUserLoading = false;
      state.groupUser.list = [];
      state.groupUser.total = 0;
    },
    fetchPriceBreakUpStart: (state) => {
      state.priceBreakUpLoading = true;
    },
    fetchPriceBreakUpSuccess: (state, action) => {
      state.priceBreakUpLoading = false;
      state.priceBreakUp.list = action.payload?.list ?? [];
      state.priceBreakUp.total = action.payload?.total ?? 0;
    },
    fetchPriceBreakUpFail: (state, action) => {
      state.priceBreakUpLoading = false;
      state.priceBreakUp.list = [];
      state.priceBreakUp.total = 0;
      state.error = action.payload;
    },
    resetPriceBreakUp: (state) => {
      state.priceBreakUpLoading = false;
      state.priceBreakUp.list = [];
      state.priceBreakUp.total = 0;
    }
  }
});

export const {
  fetchTransListStart,
  fetchTransListSuccess,
  fetchTransListFail,
  downloadTransactionStart,
  downloadTransactionSuccess,
  downloadTransactionFail,
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail,
  setGroups,
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupsFail,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  mockCardStart,
  mockCardSuccess,
  mockCardFail,
  getNoteStart,
  getNoteSuccess,
  getNoteFail,
  changeNoteStart,
  changeNoteSuccess,
  changeNoteFail,
  deletePhotoStart,
  deletePhotoSuccess,
  deletePhotoFail,
  fetchWalletDetailStart,
  fetchWalletDetailSuccess,
  fetchWalletDetailFail,
  fetchUserDetailStart,
  fetchUserDetailSuccess,
  fetchUserDetailFail,
  updateGstAmountStart,
  updateGstAmountSuccess,
  updateGstAmountFail,
  setAddTransactionLoading,
  addTransactionSuccess,
  addTransactionFail,
  getParticipantWalletStart,
  getParticipantWalletSuccess,
  getParticipantWalletFail,
  blockMerchantStart,
  blockMerchantSuccess,
  blockMerchantFail,
  reverseCashTransactionStart,
  reverseCashTransactionSuccess,
  reverseCashTransactionFail,
  fetchMccStart,
  fetchMccSuccess,
  fetchMccFail,
  updateActivityStart,
  updateActivitySuccess,
  updateActivityFail,
  fetchGroupUserListStart,
  fetchGroupUserListSuccess,
  fetchGroupUserListFail,
  fetchPriceBreakUpStart,
  fetchPriceBreakUpSuccess,
  fetchPriceBreakUpFail
} = transactionSlice.actions;

export const { setUsers, setWallets, resetGroupUserList, resetPriceBreakUp } = transactionSlice.actions;

export const fetchTransListAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransListStart());
    try {
      const response = await getAllTransaction(data);
      const structuredResponse = response.data.search_activity.activity_listing.map((activity: ActivityResponse) =>
        mapActivityToTransaction(activity)
      );

      dispatch(
        fetchTransListSuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
      return response;
    } catch (err) {
      dispatch(fetchTransListFail(err));
    }
  };
};
export const fetchTransDataAction = (data: getActivityRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransListStart());
    try {
      const response = await getActivity(data);
      const structuredResponse = response.data.get_activity;

      let receiptAvailable = structuredResponse.receipt_available;

      try {
        const photoResponse = await transactionPhoto("RECEIPT", structuredResponse.activity_id);
        const assets = photoResponse?.data?.list_assets?.asset;

        if (assets && assets.length > 0) {
          receiptAvailable = true;
        } else {
          receiptAvailable = false;
        }
      } catch (photoErr) {
        console.error("Error fetching receipt photo:", photoErr);
      }

      const formattedResponse = {
        ...mapActivityToTransaction(structuredResponse),
        receipt_available: receiptAvailable
      };

      dispatch(
        fetchTransListSuccess({
          list: [formattedResponse],
          total: 1
        })
      );
      return response;
    } catch (err) {
      dispatch(fetchTransListFail(err));
    }
  };
};

export const downloadTransactionAction = (data: TransactionDownloadRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadTransactionStart());
    try {
      const response = await downloadTransaction(data);
      const reportId = response.data.download_transactions.report_id;

      dispatch(downloadTransactionSuccess(reportId));

      // Show the report status popup using function-based API
      showReportStatusPopup({
        reportId,
        fileName: data?.report_name || "transaction",
        onNavigateToReports: () => {
          window.location.href = "/reports";
        }
      });
    } catch (err) {
      dispatch(downloadTransactionFail(err));
    }
  };
};

export const fetchUserListAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser({
        user_filter: data.user_filter,
        size: data.size,
        from: data.from
      });

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user.user_id,
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        email: user.email,
        role: "not available",
        status: user.is_active,
        userId: user.user_id
      }));

      dispatch(
        fetchUsersSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const fetchGroupUserListAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupUserListStart());
    try {
      const response = await getGroupUser(groupId);
      const structuredResponse = response.data.get_group_users.map((user: any) => ({
        id: user.user_id,
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        email: user.email,
        status: user.is_active,
        userId: user.user_id
      }));

      dispatch(
        fetchGroupUserListSuccess({
          list: structuredResponse,
          total: response.data.get_group_users.length
        })
      );
    } catch (error) {
      dispatch(fetchGroupUserListFail(error));
    }
  };
};

export const fetchWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      const response = await searchWallet(data);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        status: wallet.wallet_status,
        name: wallet.name,
        preferences: {
          cashWithdrawalAllowed: wallet?.preference_attributes?.is_cash_withdrawal_allowed,
          maxAllowedLimit: wallet?.preference_attributes?.maximum_allowed_limit,
          preferenceId: wallet?.preference_attributes?.preference_id,
          spendFrequency: wallet?.preference_attributes?.spend_frequency,
          spendLimit: wallet?.preference_attributes?.spend_limit,
          blockedMerchants: wallet?.preference_attributes?.blocked_merchant_ids,
          blockedMccCategories: wallet?.preference_attributes?.blocked_mcc_categories,
          isTransferAllowed: wallet?.preference_attributes?.is_transfer_allowed,
          xero_account_id: wallet?.preference_attributes?.xero_account_id
        },
        participant: {
          role: "PARTICIPANT",
          fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
          email: wallet?.user?.email
        }
      }));

      dispatch(
        fetchWalletsSuccess({
          list: structuredResponse,
          total: response.data.search_wallet.total_size
        })
      );
    } catch (error) {
      dispatch(fetchWalletsFail(error));
    }
  };
};

export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupsStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group.items;

      dispatch(
        fetchGroupsSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupsFail(error));
    }
  };
};

export const mockPurchaseAction = (cardId: string, amount: number, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(mockCardStart());
    try {
      const response = await mockCardPurchase(cardId, amount);

      immediateToast("success", {
        message: "Mock purchase successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(mockCardSuccess());
      if (cb) cb();
      return response;
    } catch (err) {
      dispatch(mockCardFail(err));
    }
  };
};

export const getNoteAction = (transactionId: string, expenseId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getNoteStart());
    try {
      const response = await listNote(transactionId);
      let structuredResponse = response.data.list_notes.note.map((data: any) => ({
        noteId: data.note_id,
        message: data.payload.message,
        updated_at: data.updated_at,
        created_by: data.created_by
      }));

      if (expenseId) {
        const expenseResponse = await listNote(expenseId);
        const expenseNotes = expenseResponse.data.list_notes.note.map((data: any) => ({
          noteId: data.note_id,
          message: data.payload.message,
          updated_at: data.updated_at,
          created_by: data.created_by
        }));

        structuredResponse = [...structuredResponse, ...expenseNotes];
      }

      dispatch(getNoteSuccess(structuredResponse));
    } catch (err) {
      dispatch(getNoteFail(err));
    }
  };
};

export const createNoteAction = (data: CreateNoteRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(changeNoteStart());
    try {
      await createNote(data);
      dispatch(changeNoteSuccess());
      if (cb) cb();
    } catch (err) {
      dispatch(changeNoteFail(err));
    }
  };
};

export const updateNoteAction = (noteId: string, note: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(changeNoteStart());
    try {
      await updateNote(noteId, note);
      dispatch(changeNoteSuccess());
      if (cb) cb();
    } catch (err) {
      dispatch(changeNoteFail(err));
    }
  };
};

export const deleteFile = (assetId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(deletePhotoStart());
    try {
      await deletePhoto(assetId);

      immediateToast("success", {
        message: "photo successfully deleted.",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(deletePhotoSuccess());
      if (cb) cb();
    } catch (err) {
      dispatch(deletePhotoFail(err));
    }
  };
};

export const fetchWalletDetailAction = (data: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletDetailStart());
    try {
      const walletResponse = await getWalletDetail(data);
      const wallet = walletResponse.data.get_wallet;
      const structuredWalletResponse = {
        name: wallet?.name,
        description: wallet?.description,
        balance: wallet?.balance,
        id: wallet?.wallet_id
      };

      dispatch(
        fetchWalletDetailSuccess({
          wallet: structuredWalletResponse
        })
      );
      if (cb) cb();
    } catch (error) {
      dispatch(fetchWalletDetailFail(error));
    }
  };
};

export const fetchUserDetailAction = (data: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserDetailStart());
    try {
      const response = await getUserById({ user_id: data });
      const user = response.data.get_user;

      dispatch(
        fetchUserDetailSuccess({
          user: {
            id: user.user_id,
            initial: user.attributes.initial,
            firstName: user.attributes.first_name,
            lastName: user.attributes.last_name,
            email: user.email
          }
        })
      );
    } catch (error) {
      dispatch(fetchUserDetailFail(error));
    }
  };
};

export const updateGstAmountAction = (id: string, amount: number) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateGstAmountStart());
    try {
      await updateGstAmount(id, amount);
      dispatch(updateGstAmountSuccess());
    } catch (error) {
      dispatch(updateGstAmountFail(error));
    }
  };
};

export const updateActivityAction = (data: updateActivityRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateActivityStart());
    try {
      await updateActivity(data);
      immediateToast("success", {
        message: "Transaction updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(updateActivitySuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(updateActivityFail(error));
    }
  };
};

export const addTransactionRecord = (data: adjustTransactionRequest, cb?: (_value: boolean) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setAddTransactionLoading(true));
    try {
      const response = await adjustTransaction(data);
      const { link_id, owner_id } = response.data.adjust_cash_transaction;

      await new Promise((resolve) => setTimeout(resolve, 3000));

      const activityresponse = await getActivity({ link_id, owner_id });
      const structuredResponse = activityresponse.data.get_activity;

      if (structuredResponse) {
        const formattedResponse = mapActivityToTransaction(structuredResponse);

        dispatch(
          addTransactionSuccess({
            list: [formattedResponse],
            total: 1
          })
        );
        dispatch(setAddTransactionLoading(false));
        if (cb) cb(true);
      } else {
        dispatch(setAddTransactionLoading(false));
        if (cb) cb(false);
      }
    } catch (error) {
      dispatch(addTransactionFail(error));
      dispatch(setAddTransactionLoading(false));
    }
  };
};

export const getParticipantWallet = (data: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getParticipantWalletStart());
    try {
      const walletResponse = await getWalletDetail(data);
      const wallet = walletResponse.data.get_wallet;
      const structuredWalletResponse = {
        name: wallet?.name,
        description: wallet?.description,
        balance: wallet?.balance,
        id: wallet?.wallet_id,
        displayId: wallet?.display_id,
        participant: {
          id: wallet?.user?.user_id,
          fullName: formatFullName(wallet?.user?.attributes?.first_name, wallet?.user?.attributes?.last_name),
          email: wallet?.user?.email
        },
        preferences: {
          cashWithdrawalAllowed: wallet?.preferences?.is_cash_withdrawal_allowed,
          maxAllowedLimit: wallet?.preferences?.maximum_allowed_limit,
          preferenceId: wallet?.preferences?.preference_id,
          spendFrequency: wallet?.preferences?.spend_frequency,
          spendLimit: wallet?.preferences?.spend_limit,
          blockedMerchants: wallet?.preferences?.blocked_merchant_ids,
          blockedMccCategories: wallet?.preferences?.blocked_mcc_categories
        }
      };

      dispatch(
        getParticipantWalletSuccess({
          wallet: structuredWalletResponse
        })
      );
      if (cb) cb();
    } catch (error) {
      dispatch(getParticipantWalletFail(error));
    }
  };
};

export const blockMerchantAction = (data: { wallet_ids: string[]; merchant_id: string }, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(blockMerchantStart());
    try {
      await blockMerchant(data);
      dispatch(blockMerchantSuccess());
      immediateToast("success", {
        message: "Merchant blocked successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(blockMerchantFail(error));
    }
  };
};

export const reverseCashTransactionAction = (
  data: ReverseCashTransactionRequest,
  scb?: () => void,
  fcb?: (_error: unknown) => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(reverseCashTransactionStart());
    try {
      const response = await reverseCashTransaction(data);
      const balance = response?.data?.reverse_cash_transaction?.balance_after_txn;

      dispatch(reverseCashTransactionSuccess());
      immediateToast("success", {
        // The credit row is projected asynchronously, so it is usually not in the list yet.
        message:
          typeof balance === "number"
            ? `Cash transaction reversed. The wallet balance is now ${formatSignedDollar(balance)}. The reversal appears as its own row shortly.`
            : "Cash transaction reversed. The reversal appears as its own row shortly.",
        timeout: 5000,
        position: "topCenter"
      });
      scb?.();
    } catch (error) {
      dispatch(reverseCashTransactionFail(error));
      fcb?.(error);
    }
  };
};

export const changePreferenceAction = (data: WalletPreferenceRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(blockMerchantStart());
    try {
      await changeWalletPreference(data);
      dispatch(blockMerchantSuccess());
      immediateToast("success", {
        message: "Merchant unblocked successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(blockMerchantFail(err));
    }
  };
};

export const searchMccAction = (filter: MccRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchMccStart());
    try {
      const response = await searchMCC(filter);

      dispatch(
        fetchMccSuccess({
          list: response.data?.search_mcc?.items,
          total: response?.data?.search_mcc?.total_size
        })
      );
    } catch (err) {
      dispatch(fetchMccFail(err));
    }
  };
};

export const fetchPriceBreakUpAction = (activityId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchPriceBreakUpStart());
    try {
      const preferencesResponse = await getActivityPreferences(activityId);
      const participatedUsers = preferencesResponse?.data?.get_activity_preference?.participated_users ?? [];

      if (participatedUsers.length === 0) {
        dispatch(
          fetchPriceBreakUpSuccess({
            list: [],
            total: 0
          })
        );
        return;
      }

      const userIds = participatedUsers.map((user: { user_id: string; amount: number }) => user.user_id);

      const usersResponse = await searchUser({
        user_filter: {
          user_ids: userIds
        },
        size: userIds.length,
        from: 0
      });

      const userMap = new Map(
        usersResponse.data.search_user.user_listing.map((user: any) => [
          user.user_id,
          {
            firstName: user.attributes?.first_name ?? EMPTY_STRING,
            lastName: user.attributes?.last_name ?? EMPTY_STRING,
            email: user.email ?? EMPTY_STRING,
            role: user.current_organisation_role ?? user.roles?.[0]?.value ?? EMPTY_STRING
          }
        ])
      );

      const priceBreakUpList = participatedUsers.map((participant: { user_id: string; amount: number }) => {
        const userInfo = userMap.get(participant.user_id) ?? {
          firstName: EMPTY_STRING,
          lastName: EMPTY_STRING,
          email: EMPTY_STRING,
          role: EMPTY_STRING
        };

        return {
          user_id: participant.user_id,
          amount: participant.amount,
          ...userInfo
        };
      });

      dispatch(
        fetchPriceBreakUpSuccess({
          list: priceBreakUpList,
          total: priceBreakUpList.length
        })
      );
    } catch (err) {
      dispatch(fetchPriceBreakUpFail(err));
    }
  };
};

export default transactionSlice.reducer;
